import { Component, Input, OnInit } from "@angular/core";
import { TimeLine } from "../../model/time-line";
import { Labeler } from "../../model/labeler";
import { BehaviorSubject, firstValueFrom, Observable, Observer } from "rxjs";
import { StatusService } from "../../services/status.service";
import { PrioritiesService } from "../../services/priorities.service";
import { BranchService } from "../../services/branch.service";

import { DepartmentsService } from "../../services/departaments.service";
import { UsersService } from "../../services/users.service";
import { InvolvedService } from "../../services/involved.service";
import { Involved } from "../../model/involved";
import { AuthService } from "../../auth.service";
import { TranslateService } from "@ngx-translate/core";
import { CommentsService } from "../../services/comments.service";
import { MediaService } from "../../services/media.service";
import { DomSanitizer, SafeUrl } from "@angular/platform-browser";
import { environment } from "../../../environments/environment";
import { Media } from "../../model/media";
import { Messages } from "../../model/messages";

@Component({
  selector: "ticket-timeline",
  templateUrl: "./ticket-timeline.component.html",
  styleUrls: ["./ticket-timeline.component.css"],
})
export class TicketTimelineComponent implements OnInit {
  @Input() events: TimeLine[] = [];
  @Input() referenceLabeler: Labeler | null = null;
  messages = new BehaviorSubject<Messages>(Messages.EMPTY);
  @Input() items: TimeLine[] = [];
  testSafeUrl: SafeUrl | null = null;
  private involvedCache: { [id: number]: Involved } = {};

  constructor(
    private priority: PrioritiesService,
    private status: StatusService,
    private branch: BranchService,
    private user: UsersService,
    private department: DepartmentsService,
    private involvedService: InvolvedService,
    private authService: AuthService,
    private commentsService: CommentsService,
    private mediaService: MediaService,
    private sanitizer: DomSanitizer,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.loadEvents();
  }

  async loadEvents(): Promise<void> {
    const token = this.authService.token;

    const activeInvolvedIds = this.involvedService.InvolvedData.map(
      (i) => i.id
    );

    const filteredEvents = this.events.filter((event) => {
      if (event.topic === "ticket_involved") {
        const ref = Number(event.reference);
        const oldRef = Number(event.oldReference);
        return (
          activeInvolvedIds.includes(ref) || activeInvolvedIds.includes(oldRef)
        );
      }
      return true;
    });

    const promises = filteredEvents.map(async (event) => {
      const timestamp = event.changed ?? "";
      const user = await this.getUser(event.actor);
      let referenceLabel = "";
      let oldReferenceLabel = "";
      let commentContent = "";
      let mediaPreviewUrl: SafeUrl | null = null;

      const labeler = this.getLabeler(event.topic);

if (event.topic === "branch") {
  const labeler = this.branchLabelerByService;
  if (event.reference !== undefined) {
    try {
      referenceLabel =
        event.referenceLabel ||
        (await firstValueFrom(labeler.makeLabel(event.reference)));
    } catch {
      referenceLabel = `[${event.reference}]`;
    }
  }

  if (event.oldReference !== undefined) {
    try {
      oldReferenceLabel =
        event.oldReferenceLabel ||
        (await firstValueFrom(labeler.makeLabel(event.oldReference)));
    } catch {
      oldReferenceLabel = `[${event.oldReference}]`;
    }
  }
}


if (event.topic === "priority") {
  const labeler = this.priorityLabelerByService;
  if (event.reference !== undefined) {
    try {
      referenceLabel =
        event.referenceLabel ||
        (await firstValueFrom(labeler.makeLabel(event.reference)));
    } catch {
      referenceLabel = `[${event.reference}]`;
    }
  }

  if (event.oldReference !== undefined) {
    try {
      oldReferenceLabel =
        event.oldReferenceLabel ||
        (await firstValueFrom(labeler.makeLabel(event.oldReference)));
    } catch {
      oldReferenceLabel = `[${event.oldReference}]`;
    }
  }
}


if (event.topic === "assigned") {
  const labeler = this.userLabelerByService;
  if (event.reference !== undefined) {
    try {
      referenceLabel =
        event.referenceLabel ||
        (await firstValueFrom(labeler.makeLabel(event.reference)));
    } catch {
      referenceLabel = `[${event.reference}]`;
    }
  }

  if (event.oldReference !== undefined) {
    try {
      oldReferenceLabel =
        event.oldReferenceLabel ||
        (await firstValueFrom(labeler.makeLabel(event.oldReference)));
    } catch {
      oldReferenceLabel = `[${event.oldReference}]`;
    }
  }
}


if (event.topic === "ticket_involved") {

  const getUserLabel = async (involvedId?: number): Promise<string> => {
    if (!involvedId) return "";
    try {
      return await firstValueFrom(this.user.makeLabel(involvedId));
    } catch {
      return `[ ${involvedId}]`;
    }
  };

  if (event.reference !== undefined) {
    const involved = await this.getInvolvedById(
      token,
      Number(event.reference)
    );
    const involvedUserId = involved
      ? Number(involved.involved)
      : Number(event.reference);
    referenceLabel =
      event.referenceLabel || (await getUserLabel(involvedUserId));
  }

  if (event.oldReference !== undefined) {
    const involved = await this.getInvolvedById(
      token,
      Number(event.oldReference)
    );
    const involvedUserId = involved
      ? Number(involved.involved)
      : Number(event.oldReference);
    oldReferenceLabel =
      event.oldReferenceLabel || (await getUserLabel(involvedUserId));
  }
}



      if (event.topic === "comment" && typeof event.reference === "number") {
        try {
          commentContent = await this.getCommentContentById(
            token,
            event.reference
          );
        } catch (error) {
          console.error(`[ERROR] Failed to load comment content`, error);
        }
      }

      if (event.topic === "media" && typeof event.reference === "number") {
        const media = await this.getMediaById(token, event.reference);
        if (
          media &&
          media.contentType?.startsWith("image/") &&
          media.fileInfo?.fileName
        ) {
          const url = `${environment.API_BASE_URL}media/for-ticket/${media.ticketId}/download/${media.id}/${media.fileInfo.fileName}`;
          mediaPreviewUrl =
            await this.mediaService.downloadAuthenticatedMediaUrlAsSafeUrl(
              url,
              token
            );
        }
      }

      return {
        ...event,
        timestamp,
        timestampFormatted: this.formatTimestamp(timestamp),
        title: this.getTitle(event),
        description: this.getDescription({
          ...event,
          referenceLabel,
          oldReferenceLabel,
          commentContent,
          mediaPreviewUrl,
        }),
        user,
        referenceLabel,
        oldReferenceLabel,
        commentContent,
        mediaPreviewUrl,
      };
    });

    const resolvedEvents = await Promise.all(promises);

    this.events = resolvedEvents.sort((a, b) => {
      const dateA = new Date(a.timestamp || "").getTime();
      const dateB = new Date(b.timestamp || "").getTime();
      return dateB - dateA;
    });
  }

getTitle(event: any): string {
  const action = event.action ?? "";
  const topic = event.topic ?? "";

  if (topic === "ticket") {
    if (action.includes("ticket.create"))
      return this.translate.instant("event.ticket.created");
    if (action.includes("ticket.update"))
      return this.translate.instant("event.ticket.updated");
    if (action.includes("ticket.archive"))
      return this.translate.instant("event.ticket.archived");
  }

  if (topic === "ticket_involved") {
    if (action.includes("part.create"))
      return this.translate.instant("event.ticket_involved.part_create");
    if (action.includes("part.remove"))
      return this.translate.instant("event.ticket_involved.part_remove");
  }

  if (topic === "status")
    return this.translate.instant("event.status_changed");

  if (topic === "comment")
    return this.translate.instant("event.comment_added_edited");

  if (topic === "media")
    return this.translate.instant("event.media_added");

  if (topic === "department")
    return this.translate.instant("event.department_changed");

  if (topic === "branch")
    return this.translate.instant("event.branch_changed");

  if (topic === "priority")
    return this.translate.instant("event.priority_changed");

  if (topic === "assigned")
    return this.translate.instant("event.assigned_changed");

  return this.translate.instant("event.default");
}


getDescription(event: any): string {
  const ref = event.reference ?? "-";
  const oldRef = event.oldReference ?? "-";
  const action = event.action ?? "";
  const topic = event.topic ?? "";

  if (topic === "ticket") {
    if (action.includes("ticket.create"))
      return this.translate.instant("event.desc.ticket_created");
    if (action.includes("ticket.update"))
      return this.translate.instant("event.desc.ticket_updated");
    if (action.includes("ticket.archive"))
      return this.translate.instant("event.desc.ticket_archived");
  }

  if (topic === "ticket_involved") {
    if (action.includes("part.create"))
      return this.translate.instant("event.desc.involved_added", {
        label: event.referenceLabel,
      });
    if (action.includes("part.remove"))
      return this.translate.instant("event.desc.involved_removed", {
        label: event.referenceLabel,
      });
  }

  if (topic === "status") {
    return this.translate.instant("event.desc.status_changed", {
      oldRef,
      ref,
    });
  }

  if (topic === "department") {
    return this.translate.instant("event.desc.department_changed", {
      oldRef: event.oldReferenceLabel,
      ref: event.referenceLabel,
    });
  }

  if (topic === "comment") {
    if (action.includes("part.create"))
      return this.translate.instant("event.desc.comment_added", { ref });
    if (action.includes("part.update"))
      return this.translate.instant("event.desc.comment_updated", { ref });
  }

  if (topic === "media") {
    return this.translate.instant("event.desc.media_added", { ref });
  }

  if (topic === "branch") {
    return this.translate.instant("event.desc.branch_changed", {
      oldRef: event.oldReferenceLabel,
      ref: event.referenceLabel,
    });
  }

  if (topic === "priority") {
    return this.translate.instant("event.desc.priority_changed", {
      oldRef: event.oldReferenceLabel,
      ref: event.referenceLabel,
    });
  }

  if (topic === "assigned") {
    return this.translate.instant("event.desc.assigned_changed", {
      oldRef: event.oldReferenceLabel,
      ref: event.referenceLabel,
    });
  }

  return this.translate.instant("event.desc.default");
}

  getUser(actorId: number): Promise<any> {
    return firstValueFrom(this.userLabelerByService.makeLabel(actorId)).then(
      (label) => ({ id: actorId, name: label })
    );
  }

  formatTimestamp(dateStr: string): string {
    if (!dateStr) return "";

    const cleanedDateStr = dateStr.replace(/\[.*\]/, "");

    const parsed = new Date(cleanedDateStr);

    const createdYear = parsed.getFullYear();
    const currentYear = new Date().getFullYear();

    const options: Intl.DateTimeFormatOptions =
      currentYear === createdYear
        ? {
            month: "long",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          }
        : {
            month: "long",
            day: "numeric",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          };

    return new Intl.DateTimeFormat(this.translate.currentLang, options).format(
      parsed
    );
  }

  get statusLabelerByService(): Labeler {
    return this.status;
  }
  get priorityLabelerByService(): Labeler {
    return this.priority;
  }
  get userLabelerByService(): Labeler {
    return this.user;
  }
  get departmentLabelerByService(): Labeler {
    return this.department;
  }
  get branchLabelerByService(): Labeler {
    return this.branch;
  }

  isLabeledTopic(topic: string): boolean {
    return [
      "status",
      "priority",
      "branch",
      "department",
      "ticket",
      "ticket_involved",
      "comment",
      "media",
    ].includes(topic);
  }

private getLabelersMap(): { [key: string]: Labeler } {
  return {
    status: this.status,
    priority: this.priority,
    department: this.department,
    branch: this.branch,
    assigned: this.user,
  };
}


  getLabeler(topic: string): Labeler | null {
    return this.getLabelersMap()[topic] || null;
  }

  private async getInvolvedById(
    token: string,
    involvedId: number
  ): Promise<Involved | null> {
    return new Promise((resolve) => {
      this.involvedService
        .getInvolvedById(token, involvedId)
        .subscribe((msg) => {
          const found = this.involvedService.InvolvedData.find(
            (i) => i.id === involvedId
          );
          resolve(found ?? null);
        });
    });
  }

  private async getCommentContentById(
    token: string,
    commentId: number
  ): Promise<string> {
    return new Promise((resolve, reject) => {
      this.commentsService.getCommentById(token, commentId).subscribe({
        next: () => {
          const comment = this.commentsService.commentsData.find(
            (c) => c.id === commentId
          );
          resolve(comment?.content ?? "");
        },
        error: (err) => {
          console.error("Erro ao buscar comentário por ID:", err);
          resolve("");
        },
      });
    });
  }
  private async getMediaById(
    token: string,
    mediaId: number
  ): Promise<Media | null> {
    try {
      const media = await firstValueFrom(
        this.mediaService.getMediaByIdRaw(token, mediaId)
      );
      return media;
    } catch (e) {
      console.error("Erro ao buscar mídia diretamente:", e);
      return null;
    }
  }

  removeInvolvedEvents(deletedInvolvedId: number): void {
    this.events = this.events.filter((event) => {
      const isInvolvedEvent = event.topic === "ticket_involved";
      const matchesDeletedId =
        Number(event.reference) === deletedInvolvedId ||
        Number(event.oldReference) === deletedInvolvedId;

      return !(isInvolvedEvent && matchesDeletedId);
    });
  }
}
